<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { Cloudinary } from '@cloudinary/url-gen'
import { thumbnail } from '@cloudinary/url-gen/actions/resize'
import { format, quality } from '@cloudinary/url-gen/actions/delivery'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import 'lightgallery/css/lightgallery.css'
import 'lightgallery/css/lg-thumbnail.css'
import 'lightgallery/css/lg-zoom.css'
import ContactFooter from "@/routes/ContactFooter.vue";
import { useHead } from '@vueuse/head';

const VITE_CLOUD_NAME='dmhd8lsl2';
//const VITE_UPLOAD_PRESET='photogallery';
const YEAR = "2025";
const GALLERY_TAG = `bestof-${YEAR}`;

const photos = ref([]);
const loading = ref(true);
const galleryContainer = ref(null);
let gallery = null;

const cld = new Cloudinary({
  cloud: {
    cloudName: VITE_CLOUD_NAME
  }
});

useHead({
  title: 'Annual Photo Gallery',
  meta: [
    {
      name: 'description',
      content: "Photo gallery with the personal selection of best photo shots I took in given year"
    },
  ],
  link: [
    { rel: 'icon', href: '/gallery/favicon.ico' }
  ]
});

const getData = async (tag) => {
  const response = await fetch(
    `https://res.cloudinary.com/${VITE_CLOUD_NAME}/image/list/${tag}.json`
  )
  const data = await response.json()
  photos.value = data.resources
  loading.value = false
}

const getImageUrl = (publicId, width = 300, height = 300) => {
  return cld
    .image(publicId)
    .resize(thumbnail().width(width).height(height).gravity("center"))
    .delivery(format('auto'))
    .delivery(quality('auto'))
    .toURL()
}

const getFullImageUrl = (publicId) => {
  return cld
    .image(publicId)
    .delivery(format('auto'))
    .delivery(quality('auto'))
    .toURL();
}

const initGallery = () => {
  if (galleryContainer.value && photos.value.length > 0) {
    gallery = lightGallery(galleryContainer.value, {
      plugins: [lgThumbnail, lgZoom],
      speed: 500,
      thumbnail: true,
      animateThumb: true,
      showThumbByDefault: true,
      zoom: true,
      download: false
    })
  }
}

const subTextHtml = (photo) => {
  const subTextContainer = document.createElement("div");

  const subTextHeader = document.createElement("h4");
  subTextHeader.innerText = photo.context?.custom?.caption || '';
  subTextContainer.appendChild(subTextHeader);

  const subTextDescription = document.createElement("p");
  subTextDescription.innerText = photo.context?.custom?.alt || '';
  subTextContainer.appendChild(subTextDescription);

  if (photo.context?.custom?.Location) {
    const locationName = photo.context?.custom?.Location;
    const subTextLocation = document.createElement("a");
    subTextLocation.innerText = locationName;
    const locationHref = `https://www.google.com/maps/search/${encodeURI(locationName)}`
    subTextLocation.setAttribute("href", locationHref);
    subTextLocation.setAttribute("target", "_blank");
    subTextContainer.appendChild(subTextLocation);
  }

  // Dereference
  return `${subTextContainer.innerHTML}`;
}

onMounted(async () => {
  await getData(GALLERY_TAG)
  setTimeout(() => {
    initGallery()
  }, 100)
})

onBeforeUnmount(() => {
  if (gallery) {
    gallery.destroy()
  }
})
</script>

<template>
  <section class="page-container">
    <header class="header d-flex justify-content-start">
      <div class="header-text">
        <h2>Photo Gallery</h2>
        <h6>Annual photo collection {{YEAR}}</h6>
      </div>
    </header>
    <section class="d-flex justify-content-between col-12 main-content">
      <section class="description-content">
    <template v-if="loading">
      <p class="font-bold">Loading gallery</p>
    </template>
    <template v-else-if="photos.length !== 0">
      <div ref="galleryContainer" class="d-flex flex-wrap -mx-2 justify-content-center">
          <a
              v-for="(photo, idx) in photos" :key="idx"
              :href="getFullImageUrl(photo.public_id)"
              class="hover-image m-2"
              :data-sub-html="subTextHtml(photo)"
          >
            <img
                :src="getImageUrl(photo.public_id)"
                :alt="photo.context?.custom?.alt || photo.public_id"
                class="w-full h-48 object-cover rounded shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer rounded"
            />
          </a>
      </div>
    </template>
    <template v-else>
      <p class="text-xl p-4">
        No photos found. Please make sure that you have uploaded some images using this app.
      </p>
    </template>
      </section>
    </section>
    <ContactFooter/>
  </section>
</template>

<style scoped src="./pageStyle.css"></style>
<style scoped>
  .hover-image:hover, .hover-image:focus {
    opacity: 0.8;
  }
</style>

